import {
  ArtButton,
  ArtDialog,
  ArtTable,
  ArtTableHandle,
  confirmDialog,
  HeadCell,
  SSRTableBuilder,
  useApiService,
} from "@art/shared-ui";
import { usePageWrapper, PermissionGate, usePermission } from "../../../hooks";
import { useRef, useState } from "react";
import { faEdit, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { Box, Chip, Tooltip } from "@mui/material";
import AddGroups from "./add-groups";
import { useSearchParams } from "react-router-dom";
import { tableParamNav } from "../../../utils/table-utils";

const GroupList = usePageWrapper(
  function () {
    const [queryParams, setQueryParams]: any = useSearchParams();

    const apiService = useApiService();
    const tableRef = useRef<ArtTableHandle>(null);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [selectedData, setSelectedData]: [Data | null, any] = useState(null);
    const canWriteGroups = usePermission("APP_MANAGEMENT", "GROUPS", "Write");
    const canDeleteGroups = usePermission("APP_MANAGEMENT", "GROUPS", "Delete");
    interface Data {
      id: string;
      users: { id: string; username: string }[];
      group_name: string;
    }

    const headCells: HeadCell<Data>[] = [
      {
        id: "group_name",
        numeric: false,
        disablePadding: false,
        label: "Group Name",
        getColData: (row) => row?.group_name,
      },
      {
        id: "users",
        numeric: false,
        disablePadding: false,
        label: "Users",
        getColData: (row) => {
          const users = row?.users ?? [];
          const hiddenUsers = users.slice(3);

          return (
            <div className="d-flex gap-2 flex-wrap">
              {users.slice(0, 3).map((user: any) => (
                <Chip key={user.id} size="small" label={user.username}
                  sx={{
                    '& .MuiChip-label': {
                      color: 'white',
                      padding: '16px',
                      fontSize: '14px'
                    }
                  }}
                />
              ))}
              {users.length > 3 && (
                <Tooltip
                  title={hiddenUsers.map(user => user.username).join(', ')}
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: '#04adc3',
                        '& .MuiTooltip-arrow': {
                          color: '#04adc3'
                        }
                      }
                    }
                  }}
                >
                  <Chip size="small" label={`+${users.length - 3}`}
                    sx={{
                      '& .MuiChip-label': {
                        color: 'white',
                        padding: '16px',
                        fontSize: '14px'
                      }
                    }}
                  />
                </Tooltip>
              )}
            </div>
          );
        },

      },
    ];

    const tableBuilder = SSRTableBuilder<Data>({
      endpoint: "/groups",
      method: "get",
      apiService: apiService,

      onFetch: (params) => {
        let currentPage = params["page"] || "1";
        let searchText = params["search_text"] || ""; // search

        const newParams: any = {
          page: currentPage,
        };
        if (searchText) {
          newParams.search = searchText;
        }
        setQueryParams(newParams, tableParamNav(queryParams, "page", currentPage)); // Update this

        return {
          page: currentPage,
          limit: 10,
          search_text: searchText,
        };
      },
      onResponse: (data) => {
        return {
          data: data.data.groups ?? [],
          total: data.data.total_count,
        };
      },
    });

    const openAddGroupDialog = (data?: Data) => {
      setSelectedData(data);
      setAddDialogOpen(true);
    };
    const closeAddGroupDialog = () => {
      setSelectedData(null);
      setAddDialogOpen(false);
    };

    const onSave = () => {
      closeAddGroupDialog();
      tableRef.current?.refresh(0);
    };

    return (
      <>
        <ArtDialog
          title={selectedData ? "Edit Group" : "Add Group"}
          open={addDialogOpen}
          onClose={closeAddGroupDialog}
          type="form"
          actionBuilder={(props) => {
            return (
              <Box sx={{ display: "flex", gap: "15px" }}>
                <ArtButton
                  buttonType="button"
                  dataQa="btn-save"
                  label={selectedData ? "Update" : "Add"}
                  onClick={() => props.submit?.()}
                />
                <ArtButton
                  buttonType="button"
                  dataQa="btn-reset"
                  label="Reset"
                  variant="outlined"
                  color="error"
                  onClick={() => props.reset?.()}
                />
              </Box>
            );
          }}
        >
          <AddGroups data={selectedData} onSave={onSave} />
        </ArtDialog>
        <Box className="non-clickable-table">

          <ArtTable
            ref={tableRef}
            headCells={headCells}
            builder={tableBuilder}
            search={{
              enabled: true,
              value: queryParams.get("search") || "", // check and add the search text from params
            }}
            pagination={{
              initialPage: parseInt(queryParams.get("page") || "1") - 1,
            }}
            toolbarActions={[
              <PermissionGate application="APP_MANAGEMENT" feature="GROUPS" category="Write">
                <ArtButton
                  variant="contained"
                  label="Add"
                  dataQa="btn-add"
                  icon={faPlus}
                  onClick={(_data: any) => {
                    openAddGroupDialog();
                  }}
                />
              </PermissionGate>,
            ]}
            rowActions={[
              ...(canWriteGroups ? [{
                icon: faEdit,
                label: "Edit",
                onClick: (row: Data) => {
                  openAddGroupDialog(row);
                },
              }] : []),

              ...(canDeleteGroups ? [{
                icon: faTrash,
                label: "Delete",
                onClick: (row: Data) => {
                  confirmDialog("confirm", "Do you want to delete?").then(() => {
                    apiService.deleteData(`/groups/${row?.id}`, {
                      onSuccess: () => {
                        tableRef.current?.refresh(0);
                      },
                      onError: (err: any) => {
                        console.error(err);
                      },
                    })();
                  });
                },
              }] : []),
            ]}
          />

        </Box>

      </>
    );
  },
  {
    page_title: "Groups",
  }
);

export { GroupList };
